import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-sand">
      <div className="max-w-3xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>
        
        <h1 className="font-display text-4xl font-bold text-forest mb-8">Terms of Service</h1>
        
        <div className="prose prose-forest max-w-none space-y-6 text-forest/80">
          <p className="text-sm text-muted-foreground">Last updated: March 21, 2026</p>
          
          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">1. Agreement to Terms</h2>
            <p>
              By accessing or using ProspectConnect, you agree to be bound by these Terms of Service and our Privacy Policy. These terms constitute a legally binding agreement between you and ProspectConnect. If you do not agree to these terms, you may not access or use our service.
            </p>
            <p className="mt-3">
              We reserve the right to modify these terms at any time. Material changes will be communicated via email or through the platform. Your continued use of the service after such modifications constitutes acceptance of the updated terms.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">2. Description of Service</h2>
            <p>
              ProspectConnect is a proposal management platform that enables financial advisors and professionals to create, send, and manage client proposals through a streamlined digital workflow. Our platform helps you present your services professionally, collect electronic signatures, and process client payments efficiently.
            </p>
            <p className="mt-3">
              The service is provided "as is" and we continuously work to improve functionality and reliability. We may update, modify, or discontinue features with reasonable notice when possible.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">3. Account Registration & Security</h2>
            <p>To use ProspectConnect, you must:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Provide accurate, current, and complete registration information</li>
              <li>Maintain the confidentiality and security of your account credentials</li>
              <li>Immediately notify us of any unauthorized access or security breach</li>
              <li>Be at least 18 years of age and have the legal capacity to enter into binding agreements</li>
            </ul>
            <p className="mt-3">
              You are solely responsible for all activities that occur under your account. We are not liable for any loss or damage arising from unauthorized access resulting from your failure to safeguard your credentials.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">4. Acceptable Use Policy</h2>
            <p>You agree to use ProspectConnect only for lawful purposes and in accordance with these terms. You shall not:</p>
            <ul className="list-disc pl-6 space-y-2 mt-3">
              <li>Use the service for any fraudulent, deceptive, or illegal activity</li>
              <li>Send unsolicited communications, spam, or misleading proposals</li>
              <li>Impersonate any person, entity, or misrepresent your affiliation</li>
              <li>Attempt to gain unauthorized access to our systems, networks, or other user accounts</li>
              <li>Interfere with or disrupt the integrity or performance of the service</li>
              <li>Upload or transmit malicious code, viruses, or harmful data</li>
              <li>Violate any applicable local, state, national, or international law or regulation</li>
            </ul>
            <p className="mt-3">
              Violation of this policy may result in immediate termination of your account without refund or prior notice.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">5. Payment Terms & Billing</h2>
            <p>
              Certain features of ProspectConnect may require payment. All fees are stated in US dollars and are non-refundable except as required by law or as explicitly stated otherwise.
            </p>
            <p className="mt-3">
              Payment processing is handled by secure third-party payment processors. By providing payment information, you represent that you are authorized to use the payment method and authorize us to charge applicable fees. We do not store your payment card details on our servers.
            </p>
            <p className="mt-3">
              You are responsible for all taxes associated with your use of the service, except for taxes based on our net income.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">6. Intellectual Property Rights</h2>
            <p>
              <strong>Your Content:</strong> You retain full ownership of all content, proposals, and materials you create or upload to ProspectConnect. By using our service, you grant us a limited, non-exclusive license to store, display, and transmit your content solely for the purpose of providing the service to you.
            </p>
            <p className="mt-3">
              <strong>Our Platform:</strong> ProspectConnect and its original content, features, and functionality are owned by us and are protected by international copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or reverse engineer any part of our platform without explicit written permission.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">7. Third-Party Services</h2>
            <p>
              Our platform integrates with third-party services to provide payment processing, electronic signatures, and email delivery. Your use of these integrated services is subject to their respective terms and privacy policies. We are not responsible for the practices or content of third-party services.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">8. Disclaimer of Warranties</h2>
            <p>
              ProspectConnect is provided on an "as is" and "as available" basis without warranties of any kind, whether express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p className="mt-3">
              We do not warrant that the service will be uninterrupted, error-free, or completely secure. We do not guarantee any specific results from use of the service.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">9. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by applicable law, ProspectConnect and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, business opportunities, or goodwill, arising out of or in connection with your use of the service.
            </p>
            <p className="mt-3">
              Our total liability for any claims arising from or related to these terms or the service shall not exceed the amount you paid us in the twelve (12) months preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">10. Indemnification</h2>
            <p>
              You agree to indemnify, defend, and hold harmless ProspectConnect and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable attorneys' fees) arising out of or related to your violation of these terms, your use of the service, or your violation of any rights of a third party.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">11. Termination</h2>
            <p>
              You may terminate your account at any time by using the account deletion feature in your Settings. Upon termination, your right to use the service ceases immediately and all your data will be permanently deleted.
            </p>
            <p className="mt-3">
              We may suspend or terminate your account immediately, without prior notice, if we reasonably believe you have violated these terms or engaged in conduct that could harm us, other users, or third parties. We reserve the right to refuse service to anyone for any reason at any time.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">12. Governing Law & Dispute Resolution</h2>
            <p>
              These terms shall be governed by and construed in accordance with the laws of the State of Vermont, without regard to conflict of law principles. Any disputes arising from these terms or your use of the service shall be resolved through binding arbitration, except where prohibited by law.
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-forest mt-8 mb-4">13. Contact Information</h2>
            <p>
              For questions regarding these Terms of Service, please contact us at:
            </p>
            <p className="mt-3">
              <strong>Email:</strong> <a href="mailto:support@prospectconnect.com" className="text-accent hover:underline">support@prospectconnect.com</a>
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
